import { useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { getViewMode, setViewMode, subscribeViewMode, type ViewMode } from '../lib/viewMode';

// Persistent banner for sandbox (test) users. Always visible while signed in as a
// test user, with a sandbox⇄live toggle. In LIVE view the app shows real data
// read-only (write actions are disabled via useIsReadOnlyView elsewhere).
export function TestModeBanner(): JSX.Element | null {
  const { user } = useAuth();
  const qc = useQueryClient();
  const mode = useSyncExternalStore(subscribeViewMode, getViewMode, getViewMode);

  if (!user?.isTest) return null;

  const live = mode === 'live';

  const flip = (next: ViewMode): void => {
    if (next === mode) return;
    setViewMode(next);
    // Every cached read was scoped to the old view — refetch under the new one.
    void qc.invalidateQueries();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={
        'fixed top-0 inset-x-0 z-50 text-white text-center text-sm py-2 px-3 shadow-lg flex items-center justify-center gap-3 ' +
        (live ? 'bg-rose-600' : 'bg-indigo-600')
      }
    >
      <span className="font-semibold uppercase tracking-wide">
        {live ? 'Viewing LIVE data — read only' : 'Test mode'}
      </span>
      <span className="hidden sm:inline opacity-90">
        {live
          ? 'You are looking at real data. Switch back to Sandbox to create test orders.'
          : 'Orders you create do not affect stock, accounts, commission, or reports.'}
      </span>
      <span className="inline-flex rounded-full bg-white/20 p-0.5 text-xs font-medium">
        <button
          type="button"
          onClick={() => flip('sandbox')}
          aria-pressed={!live}
          className={'rounded-full px-2.5 py-0.5 transition ' + (!live ? 'bg-white text-indigo-700' : 'text-white/90')}
        >
          Sandbox
        </button>
        <button
          type="button"
          onClick={() => flip('live')}
          aria-pressed={live}
          className={'rounded-full px-2.5 py-0.5 transition ' + (live ? 'bg-white text-rose-700' : 'text-white/90')}
        >
          Live
        </button>
      </span>
    </div>
  );
}

/**
 * True when the signed-in user is a test user currently viewing LIVE data —
 * so write actions (create / confirm / pay) must be disabled. Sandbox view and
 * real users both return false.
 */
export function useIsReadOnlyView(): boolean {
  const { user } = useAuth();
  const mode = useSyncExternalStore(subscribeViewMode, getViewMode, getViewMode);
  return !!user?.isTest && mode === 'live';
}
